import React, { useState } from 'react'

const BACKEND_URL = process.env.REACT_APP_API_BASE

const TYPE_LABEL = {
    playdynamex: 'mechanical',
    assembly: 'electronic',
    play: 'play',
    hex: 'coding',
    simulate: 'simulate',
    micropython: 'coding',
    c: 'coding',
}

/* ── Central Port Remapping Dictionary ── */
// You can add more mappings here in the future for different boards or port setups
const PORT_NAME_MAP = {
    servo_motor1: 'S1',
    servo_motor2: 'S2',
    M3: 'M2',
}

const EXTERNAL_ACCESSORIES_MAP = {
    servo_motor: 'servo_motor_180',
    analog_3_pin_input: 'toki_analog_3_pin_input',
    pwm_3_pin_output: 'toki_pwm_3_pin_output',
    digital_3_pin_input: 'toki_digital_3_pin_input',
    digital_3_pin_output: 'toki_digital_3_pin_output',
}

const INTERNAL_ACCESSORIES_MAP = {
    ir_sensor: 'toki_ir_sensor',
}

/**
 * Recursively traverses a JSON object and remaps port names
 * in specific keys ('port', 'hardwareKey', 'uiTarget', 'message') based on PORT_NAME_MAP.
 */
const remapPortsInJson = (obj, screenType) => {
    if (Array.isArray(obj)) {
        return obj.map((item) => remapPortsInJson(item, screenType))
    } else if (obj !== null && typeof obj === 'object') {
        const newObj = {}
        for (const key in obj) {
            if (
                key === 'name' &&
                screenType === 'play' &&
                typeof obj[key] === 'string'
            ) {
                const mapped = EXTERNAL_ACCESSORIES_MAP[obj[key]]
                newObj[key] = (mapped || obj[key]).toUpperCase()
            } else if (key === 'component' && typeof obj[key] === 'string') {
                newObj[key] = (
                    EXTERNAL_ACCESSORIES_MAP[obj[key]] || obj[key]
                ).toUpperCase()
            } else if (key === 'port' && typeof obj[key] === 'string') {
                // Exact match replacement for 'port' fields (Assembly & Play JSON)
                newObj[key] = PORT_NAME_MAP[obj[key]] || obj[key]
            } else if (
                // Group substring replacements together
                ['hardwareKey', 'uiTarget', 'message'].includes(key) &&
                typeof obj[key] === 'string'
            ) {
                let newKeyVal = obj[key]
                for (const [oldPort, newPort] of Object.entries(
                    PORT_NAME_MAP
                )) {
                    if (newKeyVal.includes(oldPort)) {
                        // Use split().join() or replaceAll() to catch all instances
                        // in sentences (e.g., Enable "servo_motor1".)
                        newKeyVal = newKeyVal.split(oldPort).join(newPort)
                    }
                }
                newObj[key] = newKeyVal
            } else if (
                (key === 'internalAccessories' ||
                    key === 'externalAccessories') &&
                Array.isArray(obj[key])
            ) {
                newObj[key] = obj[key].map((item) => {
                    if (typeof item !== 'string') return item

                    const normalized = item.toLowerCase().replace(/\s+/g, '_')

                    if (key === 'internalAccessories') {
                        const mapped =
                            INTERNAL_ACCESSORIES_MAP[normalized] || normalized
                        return mapped.toUpperCase()
                    } else {
                        const mapped =
                            EXTERNAL_ACCESSORIES_MAP[normalized] || normalized
                        return mapped.toUpperCase()
                    }
                })
            } else {
                // Recurse deeper into nested objects
                newObj[key] = remapPortsInJson(obj[key], screenType)
            }
        }
        return newObj
    }
    return obj
}

/* ── Reusable accordion button style ── */
const accordionBtnStyle = (isOpen) => ({
    width: '100%',
    padding: '10px 16px',
    background: isOpen ? '#2d2d30' : 'transparent',
    border: 'none',
    color: '#d4d4d4',
    cursor: 'pointer',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.8rem',
})

const chevronStyle = (isOpen) => ({
    display: 'inline-block',
    transition: 'transform .15s',
    transform: isOpen ? 'rotate(90deg)' : 'rotate(0)',
    fontSize: '0.7rem',
})

const preStyle = {
    margin: 0,
    padding: '8px 16px 12px',
    background: '#1e1e1e',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontSize: '0.75rem',
    lineHeight: 1.5,
    maxHeight: '400px',
    overflowY: 'auto',
    color: '#ce9178',
}

function JsonSidebar({ subprogram, program, onClose }) {
    const device = sessionStorage.getItem('allowedDevice') || 'Toki'

    const [expanded, setExpanded] = useState({})
    const [screenData, setScreenData] = useState({})
    const [loading, setLoading] = useState({})
    const [curriculumData, setCurriculumData] = useState(null)
    const [curriculumExpanded, setCurriculumExpanded] = useState(false)
    const [curriculumLoading, setCurriculumLoading] = useState(false)
    const [fullExpanded, setFullExpanded] = useState(false)
    const [fullData, setFullData] = useState(null)
    const [fullLoading, setFullLoading] = useState(false)

    const screens = subprogram.screens || []

    const fetchScreen = async (type, screenId) => {
        const key = `${type}/${screenId}`
        if (screenData[key]) {
            setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))
            return
        }
        setLoading((prev) => ({ ...prev, [key]: true }))
        try {
            const res = await fetch(
                `${BACKEND_URL}/api/staging/${type}/${subprogram.id}/${screenId}`,
                { credentials: 'include' }
            )
            const rawData = await res.json()

            // Clean up the JSON through our remapper before displaying it
            const data = remapPortsInJson(rawData, type)

            setScreenData((prev) => ({ ...prev, [key]: data }))
            setExpanded((prev) => ({ ...prev, [key]: true }))
        } catch (err) {
            console.error('Failed to fetch screen JSON:', err)
            setScreenData((prev) => ({
                ...prev,
                [key]: { error: err.message },
            }))
            setExpanded((prev) => ({ ...prev, [key]: true }))
        }
        setLoading((prev) => ({ ...prev, [key]: false }))
    }

    const fetchCurriculum = async () => {
        if (curriculumData) {
            setCurriculumExpanded(!curriculumExpanded)
            return
        }
        setCurriculumLoading(true)
        try {
            const res = await fetch(`${BACKEND_URL}/api/program/staging/${device.toLowerCase()}`, {
                credentials: 'include',
            })
            const data = await res.json()
            setCurriculumData(data)
            setCurriculumExpanded(true)
        } catch (err) {
            console.error('Failed to fetch curriculum:', err)
            setCurriculumData({ error: err.message })
            setCurriculumExpanded(true)
        }
        setCurriculumLoading(false)
    }

    /* Helper: fetch all screens for a single subprogram and build remapped object */
    const buildSubprogramScreens = async (sub) => {
        const subScreens = sub.screens || []
        const results = await Promise.all(
            subScreens.map(async (scr) => {
                try {
                    const res = await fetch(
                        `${BACKEND_URL}/api/staging/${scr.type}/${sub.id}/${scr.id}`,
                        { credentials: 'include' }
                    )
                    const rawData = await res.json()

                    // Clean up the JSON before it gets compiled into the full payload
                    const remappedData = remapPortsInJson(rawData, scr.type)

                    const { title, description, ...rest } = remappedData
                    return { type: scr.type, id: scr.id, data: rest }
                } catch {
                    return {
                        type: scr.type,
                        id: scr.id,
                        data: { error: 'fetch failed' },
                    }
                }
            })
        )

        const screensObj = {}
        for (const r of results) {
            const label = TYPE_LABEL[r.type] || r.type
            if (screensObj[label]) {
                if (!screensObj[label]._multi) {
                    const prev = screensObj[label]
                    screensObj[label] = { _multi: true }
                    const prevScr = subScreens.find(
                        (s) =>
                            (TYPE_LABEL[s.type] || s.type) === label &&
                            s.id !== r.id
                    )
                    if (prevScr) screensObj[label][prevScr.id] = prev
                }
                screensObj[label][r.id] = r.data
            } else {
                screensObj[label] = r.data
            }
        }
        for (const key of Object.keys(screensObj)) {
            if (screensObj[key]._multi) delete screensObj[key]._multi
        }
        return screensObj
    }

    const fetchFullJson = async () => {
        if (fullData) {
            setFullExpanded(!fullExpanded)
            return
        }
        setFullLoading(true)
        try {
            const res = await fetch(`${BACKEND_URL}/api/programs/all/staging`, {
                credentials: 'include',
            })
            const allPrograms = await res.json() // { toki: {...}, poki: {...} }

            const rawDevice = sessionStorage.getItem('connectedDevice') || ''
            const deviceSnakeCase = rawDevice.toLowerCase().replace(/\s+/g, '_')

            const programsWithScreens = {}
            for (const [key, curriculum] of Object.entries(allPrograms)) {
                const subprograms = await Promise.all(
                    (curriculum.subprograms || []).map(async (sub) => {
                        const screensObj = await buildSubprogramScreens(sub)
                        return {
                            id: sub.id,
                            title: sub.title,
                            description: sub.description,
                            link: sub.link,
                            image: sub.image,
                            screens: screensObj,
                        }
                    })
                )
                programsWithScreens[key] = {
                    id: curriculum.id,
                    title: curriculum.title,
                    description: curriculum.description,
                    // device: deviceSnakeCase,  // Don't need device name since id already tells that
                    subprograms,
                    meta: curriculum.meta || {},
                }
            }

            setFullData(programsWithScreens)
            setFullExpanded(true)
        } catch (err) {
            console.error('Failed to build full JSON:', err)
            setFullData({ error: err.message })
            setFullExpanded(true)
        }
        setFullLoading(false)
    }

    return (
        <>
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.2)',
                    zIndex: 9998,
                }}
                onClick={onClose}
            />
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: '460px',
                    maxWidth: '90vw',
                    background: '#1e1e1e',
                    color: '#d4d4d4',
                    boxShadow: '-4px 0 24px rgba(0,0,0,0.3)',
                    zIndex: 9999,
                    display: 'flex',
                    flexDirection: 'column',
                    fontFamily: "'Menlo', 'Consolas', monospace",
                    fontSize: '0.8rem',
                }}
            >
                {/* Header */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 16px',
                        borderBottom: '1px solid #333',
                        background: '#252526',
                    }}
                >
                    <span
                        style={{
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            color: '#e0e0e0',
                        }}
                    >
                        JSON Inspector
                    </span>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#999',
                            cursor: 'pointer',
                            fontSize: '1.1rem',
                            padding: '2px 6px',
                        }}
                    >
                        ✕
                    </button>
                </div>

                {/* Scrollable content */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
                    {/* Screen accordions */}
                    {screens.length === 0 && (
                        <div
                            style={{
                                padding: '16px',
                                color: '#888',
                                textAlign: 'center',
                            }}
                        >
                            No screens configured.
                        </div>
                    )}
                    {screens.map((scr, idx) => {
                        const key = `${scr.type}/${scr.id}`
                        const isOpen = expanded[key]
                        const isLoading = loading[key]
                        return (
                            <div
                                key={key + idx}
                                style={{ borderBottom: '1px solid #333' }}
                            >
                                <button
                                    onClick={() =>
                                        fetchScreen(scr.type, scr.id)
                                    }
                                    style={accordionBtnStyle(isOpen)}
                                    onMouseEnter={(e) =>
                                        (e.currentTarget.style.background =
                                            '#2d2d30')
                                    }
                                    onMouseLeave={(e) => {
                                        if (!isOpen)
                                            e.currentTarget.style.background =
                                                'transparent'
                                    }}
                                >
                                    <span style={chevronStyle(isOpen)}>▶</span>
                                    <span
                                        style={{
                                            background: '#3c3c3c',
                                            borderRadius: '3px',
                                            padding: '1px 6px',
                                            fontSize: '0.7rem',
                                            color: '#9cdcfe',
                                        }}
                                    >
                                        {idx + 1}
                                    </span>
                                    <span>{scr.id}.json</span>
                                    <span
                                        style={{
                                            color: '#888',
                                            fontSize: '0.7rem',
                                        }}
                                    >
                                        ({scr.type})
                                    </span>
                                    {isLoading && (
                                        <span
                                            style={{
                                                color: '#569cd6',
                                                marginLeft: 'auto',
                                            }}
                                        >
                                            loading…
                                        </span>
                                    )}
                                </button>
                                {isOpen && screenData[key] && (
                                    <pre style={preStyle}>
                                        {JSON.stringify(
                                            screenData[key],
                                            null,
                                            2
                                        )}
                                    </pre>
                                )}
                            </div>
                        )
                    })}

                    {/* Curriculum accordion */}
                    <div
                        style={{
                            borderTop: '2px solid #444',
                            marginTop: '4px',
                        }}
                    >
                        <button
                            onClick={fetchCurriculum}
                            style={{
                                ...accordionBtnStyle(curriculumExpanded),
                                color: '#dcdcaa',
                                fontWeight: 600,
                            }}
                            onMouseEnter={(e) =>
                                (e.currentTarget.style.background = '#2d2d30')
                            }
                            onMouseLeave={(e) => {
                                if (!curriculumExpanded)
                                    e.currentTarget.style.background =
                                        'transparent'
                            }}
                        >
                            <span style={chevronStyle(curriculumExpanded)}>
                                ▶
                            </span>
                            {device.toLowerCase()} JSON
                            <span
                                style={{
                                    color: '#888',
                                    fontSize: '0.7rem',
                                    fontWeight: 400,
                                }}
                            >
                                (overall curriculum)
                            </span>
                            {curriculumLoading && (
                                <span
                                    style={{
                                        color: '#569cd6',
                                        marginLeft: 'auto',
                                    }}
                                >
                                    loading…
                                </span>
                            )}
                        </button>
                        {curriculumExpanded && curriculumData && (
                            <pre style={{ ...preStyle, maxHeight: '500px' }}>
                                {JSON.stringify(curriculumData, null, 2)}
                            </pre>
                        )}
                    </div>

                    {/* Full JSON accordion */}
                    <div
                        style={{
                            borderTop: '2px solid #444',
                            marginTop: '4px',
                        }}
                    >
                        <button
                            onClick={fetchFullJson}
                            style={{
                                ...accordionBtnStyle(fullExpanded),
                                color: '#4ec9b0',
                                fontWeight: 600,
                            }}
                            onMouseEnter={(e) =>
                                (e.currentTarget.style.background = '#2d2d30')
                            }
                            onMouseLeave={(e) => {
                                if (!fullExpanded)
                                    e.currentTarget.style.background =
                                        'transparent'
                            }}
                        >
                            <span style={chevronStyle(fullExpanded)}>▶</span>
                            Full Curriculum JSON
                            <span
                                style={{
                                    color: '#888',
                                    fontSize: '0.7rem',
                                    fontWeight: 400,
                                }}
                            >
                                (all projects and embedded screens)
                            </span>
                            {fullLoading && (
                                <span
                                    style={{
                                        color: '#569cd6',
                                        marginLeft: 'auto',
                                    }}
                                >
                                    loading…
                                </span>
                            )}
                        </button>
                        {fullExpanded && fullData && (
                            <pre style={{ ...preStyle, maxHeight: '500px' }}>
                                {JSON.stringify(fullData, null, 2)}
                            </pre>
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}

export default JsonSidebar
